import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { styled } from "@mui/material";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import {
  createCompanyBookmark,
  deleteCompanyBookmark,
  getCompanyBookmarkByCompanyId,
  getIsLoadingCompanyBookmarkByCompanyId,
} from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import BookmarkedCompany from "marketplace/bookmarks/company-bookmarks/bookmarked-company";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";

const StyledBookmarkButton = styled(LoadingButton)<{ selected: boolean }>(({ selected, theme }) => ({
  backgroundColor: selected ? theme.palette.primary.main : undefined,
  color: selected ? theme.palette.primary.contrastText : undefined,
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: selected ? theme.palette.primary.main : undefined,
    color: selected ? theme.palette.primary.contrastText : undefined,
  },
}));

export interface CompanyBookmarkButtonProps {
  className?: string;
  companyId?: Guid;
}

export default function CompanyBookmarkButton(props: Readonly<CompanyBookmarkButtonProps>) {
  const { className, companyId } = props;

  const { enqueueSnackbar } = useSnackbar();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const existingBookmarkJson = useSelector(getCompanyBookmarkByCompanyId(companyId));
  const isLoading = useSelector(getIsLoadingCompanyBookmarkByCompanyId(companyId));

  const [existingBookmark, setExistingBookmark] = useState<BookmarkedCompany | undefined>(undefined);

  useEffect(() => {
    if (!existingBookmarkJson) return;
    setExistingBookmark(existingBookmarkJson);
  }, [existingBookmarkJson]);

  async function createBookmark(companyId: Guid) {
    try {
      await dispatch(createCompanyBookmark({ companyId, session })).unwrap();
    } catch (error) {
      console.error("Failed to create company bookmark:", error);
      return undefined;
    }
  }

  async function deleteBookmark(bookmarkId: Guid): Promise<void> {
    try {
      await dispatch(deleteCompanyBookmark({ bookmarkId, session })).unwrap();
    } catch (error) {
      console.error("Failed to delete company bookmark:", error);
    }
  }

  const handleBookmarkClick = async () => {
    try {
      if (existingBookmark) {
        await deleteBookmark(existingBookmark.id);
        enqueueSnackbar("Removed bookmark", { variant: "info" });
      } else {
        if (!companyId) throw new Error("Bookmark Company: Company ID is undefined");
        await createBookmark(companyId);
        enqueueSnackbar("Added bookmark", { variant: "info" });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't save bookmark", { variant: "error" });
    }
  };

  return (
    <StyledBookmarkButton
      className={className}
      selected={existingBookmark !== undefined}
      variant="outlined"
      aria-label="Bookmark"
      loading={isLoading ?? false}
      startIcon={<BookmarkBorderIcon />}
      disableElevation
      disabled={!companyId}
      onClick={handleBookmarkClick}
    >
      {existingBookmark ? "Bookmarked" : "Bookmark"}
    </StyledBookmarkButton>
  );
}
